/**
 * Resolves what an assigned seat costs, for the Quick Purchase checkout summary.
 *
 * Quick Purchase assigns seats without reading their price, so on a tenant with per-seat pricing the
 * buyer needs to see the amount to know whether the seat is worth editing. The figure is read from
 * the purchase tickets rather than from `seat.price`: the layout price is what the seat map quotes
 * before a purchase exists, while the ticket is what the backend actually charged once categories,
 * discounts and fare rules applied. It is also the formula the standard checkout uses
 * (`PurchasePricingPassengerTrip.js`), so the two screens cannot disagree about one seat.
 */

/**
 * Coerces a ticket's `taxes` to a number, treating missing, non-numeric and non-finite values as
 * zero. Zero is the right fallback here because a tenant that charges no taxes is ordinary.
 * `Infinity` is excluded deliberately: it is numeric and truthy, so a bare `Number(value) || 0`
 * would pass it through and render it as a price.
 * @param {number|string} value - Raw `taxes` field from the ticket.
 * @returns {number} The finite numeric value, or 0.
 */
const toTaxes = (value) => {
  const taxes = Number(value);
  return Number.isFinite(taxes) ? taxes : 0;
};

/**
 * Reads a ticket's `amount`, or `null` when it is missing, non-numeric or non-finite.
 *
 * The fare does not fall back to zero the way `taxes` does. Zero on a money row reads as "this seat
 * is free", while a ticket whose `amount` did not arrive means "we do not know what this seat cost",
 * and those are the two things the `number|null` contract exists to keep apart. A genuinely free
 * seat (`amount: 0`) still shows `$0`; a broken fare hides the figure instead of misquoting it.
 *
 * The empty cases are rejected before the numeric coercion on purpose: `Number(null)`, `Number('')`
 * and `Number('  ')` all produce a finite `0`, so leaving them to `Number.isFinite` would quote a
 * free seat for a fare that never arrived — the one confusion this function exists to prevent.
 * @param {number|string} value - Raw `amount` field from the ticket.
 * @returns {number|null} The finite numeric fare, or `null`.
 */
const toFare = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const fare = Number(value);
  return Number.isFinite(fare) ? fare : null;
};

/**
 * Sums what a passenger's seat costs on one way of the trip.
 *
 * Tickets are summed rather than found: `parsePurchase` flattens one ticket per fragment into
 * `reservation.tickets`, so an itinerary with connections gives a passenger several tickets, all of
 * them for the single seat the card is showing.
 *
 * @param {Array<object>} tickets - The way's tickets, each with `passengerId`, `amount` and `taxes`.
 * @param {string} passengerId - The passenger whose seat is being priced.
 * @returns {number|null} The seat's amount, or `null` when no ticket backs it or a matching ticket
 *   carries no usable fare.
 */
export const getPassengerSeatAmount = (tickets, passengerId) => {
  if (!Array.isArray(tickets) || !passengerId) return null;

  const matching = tickets.filter((ticket) => ticket?.passengerId === passengerId);
  if (matching.length === 0) return null;

  return matching.reduce((total, ticket) => {
    if (total === null) return null;
    const fare = toFare(ticket.amount);
    if (fare === null) return null;
    return total + fare + toTaxes(ticket.taxes);
  }, 0);
};

export default getPassengerSeatAmount;
